import {
  Card,
  Text,
  Stack,
  rem,
  Skeleton,
  Tooltip,
  Progress,
  MantineColor,
} from "@mantine/core";
import { createStyles } from "@mantine/emotion";
import {
  IconArrowUpRight,
  IconArrowDownRight,
  IconMinus,
  IconTrophy,
  IconUsers,
  IconCalendarEvent,
  IconCoin,
  IconChartBar,
  IconTicket,
  IconDeviceGamepad2,
} from "@tabler/icons-react";
import { ReactNode } from "react";

const useStyles = createStyles((theme) => ({
  card: {
    backgroundColor: theme.colors.dark[7],
    border: `1px solid ${theme.colors.dark[5]}`,
    transition: "border-color 200ms ease",
    "&:hover": {
      borderColor: theme.colors.dark[4],
    },
  },
  value: {
    fontSize: rem(24),
    fontWeight: 700,
    lineHeight: 1,
    color: theme.colors.dark[0],
  },
  label: {
    fontSize: theme.fontSizes.sm,
    fontWeight: 500,
    color: theme.colors.dark[2],
    textTransform: "uppercase",
    letterSpacing: "0.05em",
  },
  diff: {
    fontSize: theme.fontSizes.xs,
    fontWeight: 600,
    display: "flex",
    alignItems: "center",
    gap: rem(4),
  },
  diffPositive: {
    color: theme.colors.green[5],
  },
  diffNegative: {
    color: theme.colors.rose[5],
  },
  diffNeutral: {
    color: theme.colors.dark[3],
  },
  labelRow: {
    display: "flex",
    alignItems: "center",
    gap: rem(6),
  },
  progressLabel: {
    fontSize: theme.fontSizes.xs,
    color: theme.colors.dark[3],
    marginTop: rem(4),
  },
}));

// Icon mapping
const iconMap: Record<string, React.FC<{ size?: number; stroke?: number }>> = {
  tournament: IconTrophy,
  users: IconUsers,
  players: IconUsers,
  event: IconCalendarEvent,
  currency: IconCoin,
  revenue: IconCoin,
  stats: IconChartBar,
  tickets: IconTicket,
  games: IconDeviceGamepad2,
};

export interface StatCardProps {
  label: string;
  value: string | number;
  description?: string;
  diff?: number;
  diffLabel?: string;
  icon?: ReactNode;
  iconType?: keyof typeof iconMap;
  iconColor?: MantineColor;
  loading?: boolean;
  progress?: number;
  progressLabel?: string;
  progressColor?: MantineColor;
  color?: MantineColor;
  onClick?: () => void;
  tooltip?: string;
  className?: string;
}

export const StatCard = ({
  label,
  value,
  description,
  diff,
  diffLabel,
  icon,
  iconType = "stats",
  iconColor = "cyan",
  loading = false,
  progress,
  progressLabel,
  progressColor = "cyan",
  color,
  onClick,
  tooltip,
  className,
}: StatCardProps) => {
  const { classes, cx } = useStyles();

  const IconComponent = iconMap[iconType] ?? iconMap.stats;

  // Determine diff direction and style
  const getDiffStyle = () => {
    if (diff === undefined || diff === null) return null;
    if (diff > 0) return classes.diffPositive;
    if (diff < 0) return classes.diffNegative;
    return classes.diffNeutral;
  };

  const getDiffIcon = () => {
    if (diff === undefined || diff === null) return null;
    if (diff > 0) return <IconArrowUpRight size={14} stroke={2.5} />;
    if (diff < 0) return <IconArrowDownRight size={14} stroke={2.5} />;
    return <IconMinus size={14} stroke={2.5} />;
  };

  // Format value
  const formatValue = (val: string | number): string => {
    if (typeof val === "number") {
      if (val >= 1000000) {
        return `${(val / 1000000).toFixed(1)}M`;
      }
      if (val >= 1000) {
        return `${(val / 1000).toFixed(1)}K`;
      }
      return val.toLocaleString();
    }
    return val;
  };

  const content = (
    <Card
      className={cx(classes.card, className)}
      p="md"
      radius="lg"
      onClick={onClick}
      style={{ cursor: onClick ? "pointer" : undefined }}
    >
      <Stack gap="xs">
        {/* Label with inline icon */}
        <div className={classes.labelRow}>
          {icon ?? <IconComponent size={16} stroke={1.5} />}
          <Text className={classes.label}>{label}</Text>
        </div>

        {/* Value */}
        {loading ? (
          <Skeleton height={24} width="60%" radius="sm" />
        ) : (
          <Text className={classes.value} c={color}>
            {formatValue(value)}
          </Text>
        )}

        {/* Description or Diff */}
        {loading ? (
          <Skeleton height={14} width="40%" radius="sm" />
        ) : (
          <>
            {diff !== undefined && (
              <Text className={cx(classes.diff, getDiffStyle())}>
                {getDiffIcon()}
                {Math.abs(diff)}% {diffLabel ?? "vs last period"}
              </Text>
            )}

            {description && !diff && (
              <Text size="xs" c="dimmed">
                {description}
              </Text>
            )}
          </>
        )}

        {/* Progress bar */}
        {progress !== undefined && (
          <>
            <Progress
              value={progress}
              color={progressColor}
              size="sm"
              radius="xl"
              mt="xs"
            />
            {progressLabel && (
              <Text className={classes.progressLabel}>{progressLabel}</Text>
            )}
          </>
        )}
      </Stack>
    </Card>
  );

  if (tooltip) {
    return <Tooltip label={tooltip}>{content}</Tooltip>;
  }

  return content;
};

// Pre-configured stat card variants
export const TournamentStatCard = (
  props: Omit<StatCardProps, "iconType" | "iconColor">,
) => <StatCard iconType="tournament" iconColor="purple" {...props} />;

export const PlayerStatCard = (
  props: Omit<StatCardProps, "iconType" | "iconColor">,
) => <StatCard iconType="players" iconColor="cyan" {...props} />;

export const EventStatCard = (
  props: Omit<StatCardProps, "iconType" | "iconColor">,
) => <StatCard iconType="event" iconColor="amber" {...props} />;

export const RevenueStatCard = (
  props: Omit<StatCardProps, "iconType" | "iconColor">,
) => <StatCard iconType="revenue" iconColor="green" {...props} />;
